<main class="bg-black text-white min-h-screen overflow-x-hidden">
  <section class="relative min-h-[65vh] flex items-center pt-24 pb-14 overflow-hidden page-hero-gradient">
    <div class="absolute -top-48 right-0 w-[700px] h-[700px] bg-varroc-accent/5 rounded-full blur-3xl"></div>
    <div class="absolute inset-0 opacity-[0.025]" style="background-image: linear-gradient(rgba(0,170,230,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,170,230,0.5) 1px, transparent 1px); background-size: 60px 60px;"></div>

    <div class="relative z-10 max-w-7xl mx-auto px-6 w-full">
      <div class="max-w-4xl">
        <a routerLink="/products/connectiq" class="back-nav" data-aos="fade-down">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 svg-stroke-gradient"><polyline points="15 18 9 12 15 6"></polyline></svg>
          Back to ConnectIQ
        </a>
        <h1 class="hero-col font-black mb-5" data-aos="fade-up" data-aos-delay="100">
          OASIS <span class="text-varroc-accent">OTA Management</span> Platform for Connected Vehicle Programs
        </h1>
        <p class="text-varroc-text-gray text-lg leading-relaxed mb-8 max-w-3xl" data-aos="fade-up" data-aos-delay="200">
          OASIS gives OEMs an end-to-end control plane for FOTA and COTA programs, from package preparation and campaign orchestration to secure delivery, live monitoring, rollback handling, and long-term device trust.
        </p>
        <div class="flex flex-wrap gap-4" data-aos="fade-up" data-aos-delay="300">
          <a routerLink="/contact-us" class="bg-varroc-accent text-white font-bold px-8 py-4 rounded-md hover:bg-varroc-accent-hover hover:shadow-neon transition-all oasis_ota_management_talk_to_our_team_button">Talk to Our Team</a>
          <a routerLink="/products/connectiq/secureiq" class="border border-varroc-accent/40 font-semibold px-8 py-4 rounded-md hover:border-varroc-accent hover:bg-varroc-accent/10 transition-all oasis_ota_management_explore_trust_layer_button">Explore Trust Layer</a>
        </div>
      </div>
    </div>
  </section>

  <nav class="sticky-product-nav">
    <div class="max-w-7xl mx-auto px-6">
      <div class="flex overflow-x-auto">
        <a *ngFor="let s of sections" (click)="scrollTo(s.id)" [class.active]="activeSection === s.id" class="cursor-pointer flex-shrink-0">
          <i class='bx text-base' [class]="s.icon"></i> {{ s.label }}
        </a>
      </div>
    </div>
  </nav>

  <div class="max-w-7xl mx-auto px-6 py-20 space-y-28">

    <section id="overview" class="section-anchor">
      <div class="grid lg:grid-cols-[1.1fr,0.9fr] gap-10 items-start">
        <div data-aos="fade-right">
          <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Overview</span>
          <h2 class="text-4xl font-black mt-2 mb-4">A Unified OTA Control Plane for Vehicle Software and Content</h2>
          <p class="text-varroc-text-gray text-lg leading-relaxed mb-5">OASIS empowers OEMs with one operational dashboard to manage, deploy, and monitor firmware and content updates across connected vehicle ecosystems. It brings campaign control, delivery assurance, fleet segmentation, and execution visibility into a single system.</p>
          <p class="text-varroc-text-gray leading-relaxed">Designed for scale, security, and real-time insight, the platform helps teams control the full OTA lifecycle from package creation and targeting to in-vehicle execution, diagnostics, and trust management.</p>
        </div>
        <div class="grid sm:grid-cols-3 gap-5" data-aos="fade-left">
          <div *ngFor="let item of overviewHighlights" class="feature-card text-center">
            <div class="text-varroc-accent font-black text-2xl mb-2">{{ item.val }}</div>
            <div class="text-varroc-text-gray text-sm leading-relaxed">{{ item.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <section id="capabilities" class="section-anchor">
      <div class="text-center mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Capabilities</span>
        <h2 class="text-4xl font-black mt-2 mb-4">Built for Scalable, Secure OTA Execution</h2>
        <p class="text-varroc-text-gray max-w-3xl mx-auto">OASIS combines package management, campaign orchestration, delivery control, in-vehicle integration, and live monitoring so OEM teams can run complex OTA programs with confidence.</p>
      </div>
      <div class="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
        <div *ngFor="let item of capabilities; let i = index" class="feature-card" [attr.data-aos]="'fade-up'" [attr.data-aos-delay]="(i * 60).toString()">
          <div class="w-11 h-11 bg-varroc-accent/15 rounded-xl flex items-center justify-center mb-4">
            <i class='bx icon-gradient text-xl' [class]="item.icon"></i>
          </div>
          <h3 class="text-white font-bold text-lg mb-3">{{ item.title }}</h3>
          <p class="text-varroc-text-gray text-sm leading-relaxed">{{ item.desc }}</p>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <section id="usecases" class="section-anchor">
      <div class="text-center mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Use Cases</span>
        <h2 class="text-4xl font-black mt-2 mb-4">Where OASIS Delivers Operational Value</h2>
        <p class="text-varroc-text-gray max-w-3xl mx-auto">From infotainment refreshes and critical patching to campaign control and diagnostic visibility, OASIS helps OEM teams execute OTA programs with less friction and more confidence.</p>
      </div>
      <div class="grid md:grid-cols-2 gap-6">
        <div *ngFor="let item of usecases; let i = index" class="feature-card flex items-start gap-5" [attr.data-aos]="'fade-up'" [attr.data-aos-delay]="(i * 70).toString()">
          <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center flex-shrink-0">
            <i class='bx icon-gradient text-2xl' [class]="item.icon"></i>
          </div>
          <div>
            <div class="text-varroc-accent text-xs font-bold uppercase tracking-[0.25em] mb-2">{{ item.tag }}</div>
            <h3 class="text-white font-bold text-lg mb-2">{{ item.title }}</h3>
            <p class="text-varroc-text-gray text-sm leading-relaxed">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <section id="certificate" class="section-anchor">
      <div class="mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Certificate Management</span>
        <h2 class="text-4xl font-black mt-2 mb-4">The Trust Layer Behind Every OTA Workflow</h2>
        <p class="text-varroc-text-gray max-w-3xl mr-auto">OASIS is strengthened by a centralized PKI and certificate management layer that secures device identity, communication trust, and firmware authenticity across connected automotive ecosystems.</p>
      </div>
      <div class="grid lg:grid-cols-[0.95fr,1.05fr] gap-6 items-start">
        <div class="feature-card" data-aos="fade-right">
          <h3 class="text-white font-bold text-xl mb-4">Security and Compliance Foundations</h3>
          <ul class="space-y-3">
            <li class="flex items-start gap-3 text-varroc-text-gray text-sm"><i class='bx bx-check-circle icon-gradient mt-0.5 flex-shrink-0'></i><span>End-to-end TLS-secured communication across devices, cloud services, and partner systems.</span></li>
            <li class="flex items-start gap-3 text-varroc-text-gray text-sm"><i class='bx bx-check-circle icon-gradient mt-0.5 flex-shrink-0'></i><span>Protection against spoofing, unauthorized access, and man-in-the-middle attacks.</span></li>
            <li class="flex items-start gap-3 text-varroc-text-gray text-sm"><i class='bx bx-check-circle icon-gradient mt-0.5 flex-shrink-0'></i><span>Role-based access, audit logs, and traceability for governance and regulatory alignment.</span></li>
            <li class="flex items-start gap-3 text-varroc-text-gray text-sm"><i class='bx bx-check-circle icon-gradient mt-0.5 flex-shrink-0'></i><span>Designed for large-scale automotive and IoT deployments with durable trust controls.</span></li>
          </ul>
          <div class="mt-8 pt-6 border-t border-white/10">
            <h4 class="text-white font-bold text-lg mb-3">Business Impact</h4>
            <div class="space-y-3">
              <div *ngFor="let item of businessImpact" class="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-varroc-text-gray text-sm leading-relaxed">
                {{ item }}
              </div>
            </div>
          </div>
        </div>
        <div class="grid sm:grid-cols-2 gap-6" data-aos="fade-left">
          <div *ngFor="let item of certificateCapabilities" class="feature-card h-full">
            <div class="w-11 h-11 bg-varroc-accent/15 rounded-xl flex items-center justify-center mb-4">
              <i class='bx icon-gradient text-xl' [class]="item.icon"></i>
            </div>
            <h3 class="text-white font-bold text-lg mb-3">{{ item.title }}</h3>
            <p class="text-varroc-text-gray text-sm leading-relaxed">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <section id="workflow" class="section-anchor">
      <div class="mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Workflow</span>
        <h2 class="text-4xl font-black mt-2 mb-4">OASIS OTA Workflow</h2>
        <p class="text-varroc-text-gray max-w-3xl mr-auto">A visual view of how OASIS coordinates package preparation, trust enforcement, campaign control, delivery, and in-vehicle execution across the OTA lifecycle.</p>
      </div>
      <div class="feature-card overflow-hidden" data-aos="fade-up">
        <img [src]="workflowImage" alt="OASIS OTA workflow diagram" class="w-full h-auto rounded-xl block">
      </div>
    </section>

  

  </div>
</main>
